// Shared helpers for user types, groups and application access (docs/access.md).
import { useQuery } from "@tanstack/react-query";
import type { ClientAccessDto, GroupDto, UserTypeDto } from "@platform/core";
import { Badge, type MultiSelectOption } from "@platform/ui";
import { useApi, useConsole } from "../context";
import { useT, type TFunc } from "../i18n";

/** The console's own OAuth client: its access is managed by the platform (read-only here). */
export function isConsoleClient(clientId: string, consoleClientId: string): boolean {
  return clientId === consoleClientId || clientId === "platform-console";
}

export function useUserTypes(enabled = true) {
  const api = useApi();
  const { can } = useConsole();
  return useQuery({
    queryKey: ["user-types"],
    queryFn: api.userTypes.list,
    enabled: enabled && can("settings:read"),
  });
}

export function useGroups(enabled = true) {
  const api = useApi();
  const { can } = useConsole();
  return useQuery({
    queryKey: ["groups"],
    queryFn: api.groups.list,
    enabled: enabled && can("groups:read"),
  });
}

export function typeOptions(
  types: readonly Pick<UserTypeDto, "id" | "name">[] | undefined,
): MultiSelectOption[] {
  return (types ?? []).map((ut) => ({
    value: ut.id,
    label: ut.name === ut.id ? ut.id : `${ut.name} (${ut.id})`,
  }));
}

export function groupOptions(groups: readonly GroupDto[] | undefined): MultiSelectOption[] {
  return (groups ?? []).map((g) => ({
    value: g.id,
    label: g.name === g.id ? g.id : `${g.name} (${g.id})`,
    ...(g.description ? { description: g.description } : {}),
  }));
}

/** Short text for an access rule: "Everyone" or "employee · chat-agents". */
export function accessSummary(access: ClientAccessDto | undefined, t: TFunc): string {
  if (!access || access.mode === "all") return t("access.everyone");
  const parts = [...access.userTypes, ...access.groups];
  return parts.length ? parts.join(" · ") : t("access.nobody");
}

/** `null` = the root account (system account without a type). */
export function UserTypeBadge({ type }: { type: string | null }) {
  const { t } = useT();
  if (type === null) return <Badge tone="warning">{t("access.systemAccount")}</Badge>;
  return <Badge tone={type === "employee" ? "info" : "neutral"}>{type}</Badge>;
}

/** Lines of a textarea → trimmed, non-empty, de-duplicated values. */
export function linesToList(text: string): string[] {
  return [
    ...new Set(
      text
        .split(/[\n,]/)
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean),
    ),
  ];
}

export const SLUG_PATTERN = "[a-z][a-z0-9_-]{1,40}";
export const SLUG_RE = /^[a-z][a-z0-9_-]{1,40}$/;
